/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import type { OperationVariables, ApolloClient } from '@apollo/client'
import type { ApolloWrap } from 'nuxt-base/apollo/apolloClient.client'
import type { ProvidedValue, Props } from '../components/CatalogSearch.vue'
import type { RouteLocationNormalizedGeneric } from '#vue-router'

export function useApollo(client: 'get' | 'post'): ApolloClient {
  const clients = useNuxtApp().$apollo
  return client === 'post' ? clients.post : clients.get
}

export function useApolloWrap2<T extends Record<string, unknown>, Vars extends OperationVariables>(queryDocument: MyDocumentNode): ApolloWrap<T, Vars> {
  return useNuxtApp().$useApolloWrap2(queryDocument)
}

export function useLinks() {
  return useNuxtApp().$links
}

export function useReset(cb: () => void): void {
  const off = useNuxtApp().hook('app:reset', () => cb())
  onScopeDispose(() => off())
}

export type UserValues = {
  _fetchedAt: number
  _fullname: string
  userIdChanged: boolean
  loading: boolean
  isAuth: boolean
  userId: string | None
  email: string | None
  name: string | None
  publicName: string | None
  lastname: string | None
  isEmailVerified: boolean
  cartSize: number
  birthdate: string | None
  gender: gen.EnumBuyerGender | None
  lang: Locales
  privatePricings: { sellerId: string; hasPricing?: boolean | None }[] | None
  isB2B: boolean
  isPublic: boolean
  addressDefault: gen.BuyerAddressDefaultFragment
  buyerNotifications: boolean | None
}

export function toUserState(me: gen.AuthMeFragment | None, overwrite?: Partial<UserValues>): UserValues {
  const lang = toLang(me?.lang) ?? 'en'
  const privatePricings = helpers.arrClean(me?.user?._b2b)
  return {
    _fetchedAt: new Date().getTime(),
    _fullname: helpers.joinString(' ', me?.user?.doc.name, me?.user?.doc.lastname) || '',
    publicName: me?.user?.doc.publicName,
    userIdChanged: false,
    loading: false,
    isAuth: !!me?.isAuth && !overwrite?.loading,
    userId: me?._id,
    email: me?.user?.doc.email,
    isEmailVerified: !!me?.user?.isEmailVerified,
    name: me?.user?.doc.name,
    lastname: me?.user?.doc.lastname,
    cartSize: helpers.toPositive(me?.user?.doc.buyerCartSize, 0),
    gender: me?.user?.doc.gender,
    birthdate: me?.user?.doc.birthdate,
    isPublic: !!me?.user?.doc.isPublic,
    privatePricings,
    isB2B: !!privatePricings?.some((i) => i.sellerId === B2B_SELLER_ID),
    lang,
    addressDefault: me?.user?.doc?.address || { iso2: 'LT', countryName: { value: lang === 'lt' ? 'Lietuva' : 'Lithuania' } },
    buyerNotifications: me?.user?.doc.buyerNotifications,
    ...overwrite,
  } satisfies UserValues
}

export function updateUserDoc() {
  const userState = useState<UserValues>('auth:doc', () => shallowRef(toUserState(undefined, { loading: true })))
  return {
    prom: useState('auth:promise', () => shallowRef<{ promise: Promise<unknown> | undefined }>({ promise: undefined })),
    refetch: useState('auth:refetch', () => shallowRef<{ refetch: boolean }>({ refetch: false })),
    user: toRef(() => userState.value),
    update(overwrite?: Partial<UserValues>) {
      userState.value = { ...userState.value, ...overwrite }
    },
  }
}

export function useAuth(): Readonly<globalThis.Ref<UserValues, UserValues>> {
  const { user } = updateUserDoc()
  return user
}

export const catalogSearchContextKey: InjectionKey<ProvidedValue> = Symbol('catalogSearch')

export function useCatalogSearchContext() {
  return helpers.required(inject(catalogSearchContextKey), 'no context')
}

export function useCatalogQueryUpdate(options: Props['options']) {
  return { options }
}

export type usePrevRouteRoute = Pick<RouteLocationNormalizedGeneric, 'fullPath' | 'name' | 'params' | 'path' | 'query'>
export function usePrevRoute() {
  return useState('usePrevRoute', () => shallowRef<usePrevRouteRoute | undefined>(undefined))
}
